/* eslint-disable */
import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import StatisticsCard from "../../components/statistics/StatisticsCard";
import DeliveryStatisticsChart from "../../components/statistics/DeliveryStatisticsChart";
import { TrendingUp, TrendingDown, Truck } from "lucide-react";
import { Spin, DatePicker } from "antd";
import dayjs from "dayjs";
import { Filter } from "lucide-react";

const DeliveryStatisticsView = ({
  filteredOrders,
  dateRange,
  setDateRange,
  customDateRange,
  setCustomDateRange,
  selected,
  setSelected,
  selectedConfirmerId,
  setSelectedConfirmerId,
  confirmers,
  products,
  loading,
  getDateRangeValues,
  deliveryStatusMapping,
  t,
  isRTL,
}) => {
  const { startDate, endDate } = getDateRangeValues();

  // États pour les filtres
  const [isOpen, setIsOpen] = useState(false);
  const [isConfirmerOpen, setIsConfirmerOpen] = useState(false);

  // Calculate delivery statistics
  const deliveryStats = useMemo(() => {
    const stats = {
      delivered: 0,
      returned: 0,
      onShippingCompany: 0,
      total: 0,
    };

    filteredOrders.forEach((order) => {
      const companyStatus = order.CompanyDeleveryStatus;
      const companyStatusStr = String(companyStatus).toLowerCase();

      // Vérifier si le statut correspond à delivered
      const isDelivered = deliveryStatusMapping.delivered.some(
        val => String(val).toLowerCase() === companyStatusStr
      );
      // Vérifier si le statut correspond à returned
      const isReturned = deliveryStatusMapping.returned.some(
        val => String(val).toLowerCase() === companyStatusStr
      );
      // Vérifier si le statut correspond à on_shipping
      const isOnShipping = deliveryStatusMapping.on_shipping.some(
        val => String(val).toLowerCase() === companyStatusStr
      );

      // Compter les commandes ayant un statut de livraison
      if (isDelivered || isReturned || isOnShipping) {
        stats.total += 1;

        if (isDelivered) {
          stats.delivered += 1;
        } else if (isReturned) {
          stats.returned += 1;
        } else if (isOnShipping) {
          stats.onShippingCompany += 1;
        }
      }
    });

    return stats;
  }, [filteredOrders]);

  const toggleProduct = (name) => {
    setSelected((prev) =>
      prev.includes(name)
        ? prev.filter((pname) => pname !== name)
        : [...prev, name]
    );
  };

  const confirmerOptions = useMemo(() => {
    return confirmers.map((w) => ({
      label: w.name || w.email,
      value: w.id,
    }));
  }, [confirmers]);

  const selectedConfirmerLabel = useMemo(() => {
    if (selectedConfirmerId === "all") {
      return t("adduser.all_confirmers");
    }
    if (selectedConfirmerId === "unassigned") {
      return t("orders.datatable_templating_demo.unassigned");
    }
    const found = confirmers.find((c) => c.id === selectedConfirmerId);
    return found?.name || found?.email || t("stats.select_confirmer");
  }, [selectedConfirmerId, confirmers, t]);

  return (
    <Spin spinning={loading}>
      {/* Filters Section - Reuse from main tab */}
      <div className="bg-white dark:!bg-[#181919] rounded-xl shadow-sm p-4 mb-8 border border-purple-100 dark:!border-[#3c3a3d]">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Filter size={20} className="text-[#9e68ba]" />
            <h2 className="text-lg font-semibold text-[#9e68ba]">
              {t("stats.filters") || t("stats.filters")}
            </h2>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="bg-white dark:!bg-[#181919] rounded-xl shadow-sm border border-purple-100 dark:!border-[#3c3a3d] overflow-hidden">
            <div className="flex">
              {[
                {
                  value: "today",
                  label: t("stats.today") || "Aujourd'hui",
                },
                {
                  value: "yesterday",
                  label: t("stats.yesterday") || "Hier",
                },
                {
                  value: "week",
                  label: t("stats.week") || "Semaine",
                },
                { value: "month", label: t("stats.month") || "Mois" },
              ].map((option) => (
                <button
                  key={option.value}
                  className={`px-6 py-3 text-sm font-medium transition-colors ${
                    dateRange === option.value
                      ? "bg-purple-50 dark:!bg-[#522868] dark:!text-gray-300 text-[#9e68ba] border-b-2 border-[#9b59bf]"
                      : "text-gray-600 hover:bg-purple-50 dark:!text-gray-300"
                  }`}
                  onClick={() => {
                    setDateRange(option.value);
                    setCustomDateRange([null, null]);
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white dark:!bg-[#181919] rounded-xl shadow-sm border border-purple-100 dark:!border-[#3c3a3d]">
            <DatePicker.RangePicker
              value={
                customDateRange[0] && customDateRange[1]
                  ? [
                      dayjs(customDateRange[0]),
                      dayjs(customDateRange[1]),
                    ]
                  : null
              }
              onChange={(dates) => {
                if (dates) {
                  setCustomDateRange([
                    dates[0].toDate(),
                    dates[1].toDate(),
                  ]);
                  setDateRange("custom");
                } else {
                  setCustomDateRange([null, null]);
                }
              }}
              className="w-full"
              style={{
                backgroundColor:
                  document.documentElement.classList.contains("dark")
                    ? "#181919"
                    : "#fff",
              }}
            />
          </div>

          {/* Product Filter */}
          <div className="relative">
            <div
              className="relative cursor-pointer group"
              onClick={() => setIsOpen(!isOpen)}
            >
              <div className="flex items-center justify-between w-full px-4 py-2.5 bg-white dark:!bg-[#181919] border border-gray-200 dark:!border-[#3c3a3d] rounded-lg shadow-sm hover:shadow-md transition-all duration-200">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-[#faf1ff] rounded-full flex items-center justify-center">
                    <svg
                      className="w-3 h-3 text-[#9b59bf]"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V4z" />
                    </svg>
                  </div>

                  <span className="font-medium text-gray-900 dark:!text-gray-300">
                    {selected.length > 0
                      ? selected.length === 1
                        ? t("orders.one_product")
                        : t("orders.multiple_products", {
                            count: selected.length,
                          })
                      : t("orders.select_products")}
                  </span>
                </div>

                <div
                  className={`transform transition-transform duration-200 ${
                    isOpen ? "rotate-180" : ""
                  }`}
                >
                  <svg
                    className="w-4 h-4 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>

              {selected.length > 0 && (
                <div className="absolute -top-1 -right-1 bg-[#9b59bf] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold shadow-sm">
                  {selected.length}
                </div>
              )}
            </div>

            <div className="relative">
              {isOpen && (
                <>
                  <div
                    className="fixed inset-0 bg-opacity-5 z-40"
                    onClick={() => setIsOpen(false)}
                  />

                  <div
                    className="absolute mt-1 bg-white dark:!bg-[#181919] dark:!text-gray-300 border border-gray-200 dark:!border-[#3c3a3d] rounded-lg shadow-lg max-h-60 overflow-hidden w-full animate-in slide-in-from-top-1 duration-150"
                    style={{
                      zIndex: 100000,
                    }}
                  >
                    <div className="bg-[#faf1ff] dark:!bg-[#181919] px-4 py-2.5 border-b border-purple-100 dark:!border-[#3c3a3d]">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-800 dark:!text-gray-300 text-sm">
                          {t("orders.available_products")}
                        </span>
                        {selected.length > 0 && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelected([]);
                            }}
                            className="text-xs text-[#9b59bf] hover:text-[#814a9f] font-medium transition-colors"
                          >
                            {t("orders.clear_all")}
                          </button>
                        )}
                      </div>
                    </div>

                    <div
                      className="max-h-48 overflow-y-auto"
                      style={{
                        scrollbarWidth: "thin",
                        scrollbarColor: "#d8b4fe #f5f5f5",
                        zIndex: 100000,
                      }}
                    >
                      {products.map((product) => {
                        const isSelected = selected.includes(
                          product.name
                        );
                        return (
                          <label
                            key={product.id}
                            className={`group dark:!bg-[#181919] flex items-center gap-2 px-4 py-2.5 hover:bg-[#faf1ff] cursor-pointer transition-colors duration-150 ${
                              isSelected
                                ? "bg-[#faf1ff] border-r-2 border-[#9b59bf]"
                                : ""
                            }`}
                          >
                            <div className="relative flex-shrink-0">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => {
                                  toggleProduct(product.name);
                                  setIsOpen(false);
                                }}
                                className="sr-only"
                              />
                              <div
                                className={`w-4 h-4 rounded border transition-all duration-200 flex items-center justify-center ${
                                  isSelected
                                    ? "bg-[#9b59bf] dark:!bg-[#181919] border-[#9b59bf]"
                                    : "border-gray-300 hover:border-[#9b59bf]"
                                }`}
                              >
                                {isSelected && (
                                  <svg
                                    className="w-3 h-3 text-white"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                )}
                              </div>
                            </div>

                            <span
                              className={`text-sm transition-colors ${
                                isSelected
                                  ? "text-[#9b59bf] font-medium"
                                  : "text-gray-800 dark:!text-gray-300 group-hover:text-[#9b59bf]"
                              }`}
                            >
                              {product.name}
                            </span>

                            {isSelected && (
                              <div className="w-1.5 h-1.5 bg-[#9b59bf] rounded-full ml-auto" />
                            )}
                          </label>
                        );
                      })}
                    </div>

                    {products.length === 0 && (
                      <div className="px-4 py-8 text-center text-gray-500">
                        <div className="w-8 h-8 mx-auto mb-2 text-gray-300">
                          <svg fill="currentColor" viewBox="0 0 20 20">
                            <path d="M10 2L3 7v11a1 1 0 001 1h12a1 1 0 001-1V7l-7-5zM10 12a2 2 0 100-4 2 2 0 000 4z" />
                          </svg>
                        </div>
                        <span className="text-sm">
                          {t("orders.no_products_available")}
                        </span>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Confirmer Filter */}
          <div className="relative">
            <div
              className="relative cursor-pointer group"
              onClick={() => setIsConfirmerOpen(!isConfirmerOpen)}
            >
              <div className="flex items-center justify-between w-full px-4 py-2.5 bg-white dark:!bg-[#181919] border border-gray-200 dark:!border-[#3c3a3d] rounded-lg shadow-sm hover:shadow-md transition-all duration-200">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-[#faf1ff] rounded-full flex items-center justify-center">
                    <svg
                      className="w-3 h-3 text-[#9b59bf]"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M10 2a8 8 0 100 16 8 8 0 000-16zm3 11H7a1 1 0 110-2h6a1 1 0 110 2zm0-4H7a1 1 0 110-2h6a1 1 0 110 2z" />
                    </svg>
                  </div>

                  <span className="font-medium text-gray-900 dark:!text-gray-300">
                    {selectedConfirmerLabel}
                  </span>
                </div>

                <div
                  className={`transform transition-transform duration-200 ${
                    isConfirmerOpen ? "rotate-180" : ""
                  }`}
                >
                  <svg
                    className="w-4 h-4 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </div>

            <div className="relative">
              {isConfirmerOpen && (
                <>
                  <div
                    className="fixed inset-0 bg-opacity-5 z-40"
                    onClick={() => setIsConfirmerOpen(false)}
                  />

                  <div
                    className="absolute mt-1 bg-white dark:!bg-[#181919] dark:!text-gray-300 border border-gray-200 dark:!border-[#3c3a3d] rounded-lg shadow-lg max-h-60 overflow-hidden w-full animate-in slide-in-from-top-1 duration-150"
                    style={{
                      zIndex: 100000,
                    }}
                  >
                    <div className="bg-[#faf1ff] dark:!bg-[#181919] px-4 py-2.5 border-b border-purple-100 dark:!border-[#3c3a3d]">
                      <span className="font-medium text-gray-800 dark:!text-gray-300 text-sm">
                        {t("stats.confirmer") || t("stats.confirmer")}
                      </span>
                    </div>

                    <div className="max-h-48 overflow-y-auto">
                      {[
                        {
                          label: t("adduser.all_confirmers"),
                          value: "all",
                        },
                        {
                          label: t(
                            "orders.datatable_templating_demo.unassigned"
                          ),
                          value: "unassigned",
                        },
                        ...confirmerOptions,
                      ].map((option) => {
                        const isSelected =
                          option.value === selectedConfirmerId;
                        return (
                          <button
                            type="button"
                            key={option.value}
                            onClick={() => {
                              setSelectedConfirmerId(
                                option.value
                              );
                              setIsConfirmerOpen(false);
                            }}
                            className={`w-full text-left px-4 py-2.5 flex items-center gap-2 hover:bg-[#faf1ff] dark:hover:bg-[#1f1f1f] transition-colors ${
                              isSelected
                                ? "bg-[#faf1ff] text-[#9b59bf] font-medium"
                                : "text-gray-800 dark:!text-gray-300"
                            }`}
                          >
                            <span className="flex-1">
                              {option.label}
                            </span>
                            {isSelected && (
                              <svg
                                className="w-4 h-4 text-[#9b59bf]"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          <button
            onClick={() => {
              setDateRange("week");
              setCustomDateRange([null, null]);
              setSelected([]);
              setSelectedConfirmerId("all");
            }}
            className="ml-auto px-4 py-2 text-sm font-medium rounded-lg bg-purple-50 text-[#9e68ba] border border-purple-100 hover:bg-purple-100 dark:!bg-[#262526] dark:!text-gray-300 dark:!border-[#3c3a3d] transition-colors"
          >
            {t("stats.clear_filters") || t("stats.clear_filters")}
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <StatisticsCard
          title={t("delivery.delivered") || t("delivery.delivered")}
          count={deliveryStats.delivered}
          icon={TrendingUp}
          color="green"
          description={`${
            deliveryStats.total > 0
              ? ((deliveryStats.delivered / deliveryStats.total) * 100).toFixed(1)
              : 0
          }${t("stats.of_total") || "% du total"}`}
        />

        <StatisticsCard
          title={t("delivery.returned") || t("delivery.returned")}
          count={deliveryStats.returned}
          icon={TrendingDown}
          color="red"
          description={`${
            deliveryStats.total > 0
              ? ((deliveryStats.returned / deliveryStats.total) * 100).toFixed(1)
              : 0
          }${t("stats.of_total") || "% du total"}`}
        />

        <StatisticsCard
          title={t("delivery.on_shipping") || t("delivery.on_shipping")}
          count={deliveryStats.onShippingCompany}
          icon={Truck}
          color="blue"
          description={`${
            deliveryStats.total > 0
              ? ((deliveryStats.onShippingCompany / deliveryStats.total) * 100).toFixed(1)
              : 0
          }${t("stats.of_total") || "% du total"}`}
        />
      </div>

      {/* Chart */}
      <DeliveryStatisticsChart
        orders={filteredOrders}
        dateRange={dateRange}
        startDate={startDate}
        endDate={endDate}
        deliveryStatusMapping={deliveryStatusMapping}
      />
    </Spin>
  );
};

export default DeliveryStatisticsView;
